import React, { useState } from "react";
import { Icon, Modal, Button } from "semantic-ui-react";
import * as trialActions from "../../../../redux/actions/trialActions";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useAuth0 } from "../../../../auth/auth0";
import { produce } from "immer";
import { AerialPlotResponse } from "../../../../apis/types";
import { WorkingTrial } from "../../../../apis/types/trialTypes";

interface RemoveTrialOwnerSponsorModalProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	trialId: string;
	clientId: string;
	removeTrialOwnerOrSponsor: string;
	sponsorUserId?: string | null;
	sponsorClientId?: string | null;
	setWorkingTrialSilently: (workingTrial: WorkingTrial) => void;
	workingTrial: WorkingTrial;
}

const RemoveTrialOwnerSponsorModal = ({
	open,
	setOpen,
	trialId,
	clientId,
	removeTrialOwnerOrSponsor,
	sponsorUserId,
	sponsorClientId,
	setWorkingTrialSilently,
	workingTrial,
}: RemoveTrialOwnerSponsorModalProps) => {
	const { getTokenSilently } = useAuth0();
	const dispatch = useDispatch();
	const [isRemoving, setIsRemoving] = useState(false);

	const removeTrialOwnerSponsor = async () => {
		setIsRemoving(true);

		try {
			const accessToken = await getTokenSilently();

			if (removeTrialOwnerOrSponsor === "Owner") {
				const res = (await trialActions.removeTrialOwner(
					trialId,
					clientId,
					accessToken,
				)(dispatch)) as AerialPlotResponse<unknown>;

				if (res.statusCode === 200) {
					toast.success("Trial Owner removed successfully");

					const newWorkingTrial = produce(workingTrial, (draft) => {
						draft.trialOwnerUserId = null;
						draft.trialOwnerPersonId = null;
						draft.trialOwnerIsPending = false;
						draft.trialOwnerClientId = null;
						draft.trialOwnerClientName = null;
					});
					setWorkingTrialSilently(newWorkingTrial);
				}

				setOpen(false);
			} else if (removeTrialOwnerOrSponsor === "Sponsor") {
				const res = (await trialActions.removeTrialSponsor(
					trialId,
					clientId,
					sponsorUserId,
					sponsorClientId,
					accessToken,
				)(dispatch)) as AerialPlotResponse<unknown>;

				if (res.statusCode === 200) {
					toast.success("Trial sponsor removed successfully");

					const newWorkingTrial = produce(workingTrial, (draft) => {
						draft.trialSponsors = (draft.trialSponsors ?? []).filter((s) => s.userId !== sponsorUserId);
					});
					setWorkingTrialSilently(newWorkingTrial);
				}

				setOpen(false);
			}
		} catch {
			toast.error(`Failed to remove trial ${removeTrialOwnerOrSponsor.toLowerCase()}`);
		} finally {
			setIsRemoving(false);
		}
	};

	return (
		<Modal
			onClose={() => {
				setOpen(false);
			}}
			onOpen={() => setOpen(true)}
			open={open}
			style={{ width: "30%", border: "none" }}
		>
			<Modal.Header>
				Remove Trial{" "}
				{removeTrialOwnerOrSponsor?.slice(0, 1).toUpperCase() + removeTrialOwnerOrSponsor?.slice(1).toLowerCase()}
				<Icon
					name="close"
					link
					onClick={() => {
						setOpen(false);
					}}
				/>
			</Modal.Header>
			<Modal.Content>
				<b>
					Are you sure you want to remove trial {removeTrialOwnerOrSponsor.toLowerCase()}?{" "}
					{removeTrialOwnerOrSponsor === "Owner" ? "This will also unpublish any previously published flights." : null}
				</b>
			</Modal.Content>
			<Modal.Actions>
				<Button
					onClick={() => {
						setOpen(false);
					}}
				>
					Cancel
				</Button>
				<Button
					primary
					loading={isRemoving}
					onClick={() => {
						void removeTrialOwnerSponsor();
					}}
				>
					Confirm
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default RemoveTrialOwnerSponsorModal;
